import { For, Show, createSignal } from "solid-js";

import { DraftEmailModal } from "~/components/DraftEmailModal";
import { useAccountContacts, useContact } from "~/lib/hooks";

type Props = {
  wsKey: () => string;
  dealId: () => string;
  dealName: () => string;
  // Pre-resolved from the deal row so we don't refetch:
  primaryContactId: () => string | null;
  accountId: () => string | null;
  onClose: () => void;
};

type Stage = { kind: "picker" } | { kind: "drafting"; conId: string; contactLabel: string };

/**
 * On Deal: if the deal has a `primary_contact_id`, jump straight into
 * the DraftEmailModal with that contact + deal context. Otherwise show
 * a recipient picker from the deal's account contacts (or "no contacts
 * to pick from" if both are absent).
 *
 * `contextDealId` is threaded into the modal so the prompt mentions
 * the deal's amount/stage/next-step; `contextAccountId` is added when
 * the deal has one so the AI also sees the account's industry/stage.
 */
export function DealDraftEmailDialog(props: Props) {
  const primaryContact = useContact(props.wsKey, () => props.primaryContactId() ?? "");
  // Fallback recipient picker — only fired when no primary contact.
  const accountContacts = useAccountContacts(props.wsKey, () =>
    props.primaryContactId() === null ? (props.accountId() ?? "") : "",
  );

  const initial = (): Stage => {
    const pcid = props.primaryContactId();
    if (pcid && primaryContact.data) {
      return {
        kind: "drafting",
        conId: pcid,
        contactLabel: primaryContact.data.display_name ?? primaryContact.data.full_name,
      };
    }
    if (pcid && !primaryContact.data) {
      // Still resolving the primary contact's name — show picker for
      // now; the parent rerenders once data lands.
      return { kind: "picker" };
    }
    return { kind: "picker" };
  };
  const [stage, setStage] = createSignal<Stage>(initial());

  // If the primary-contact fetch resolves after mount, transition into
  // drafting automatically (without forcing the picker on the user).
  const tryAutoAdvance = () => {
    if (stage().kind !== "picker") return;
    const pcid = props.primaryContactId();
    if (pcid && primaryContact.data) {
      setStage({
        kind: "drafting",
        conId: pcid,
        contactLabel: primaryContact.data.display_name ?? primaryContact.data.full_name,
      });
    }
  };
  tryAutoAdvance();

  return (
    <>
      <Show when={stage().kind === "picker"}>
        <dialog
          open
          class="modal modal-open"
          aria-modal="true"
          aria-labelledby="deal-draft-picker-title"
        >
          <div class="modal-box max-w-lg">
            <h3 id="deal-draft-picker-title" class="font-bold text-lg mb-2">
              ✨ Draft email on behalf of {props.dealName()}
            </h3>

            <Show when={props.primaryContactId() === null && props.accountId() === null}>
              <p class="text-sm opacity-60 py-4">
                This deal has no primary contact and isn't linked to an account. Set one of those
                first.
              </p>
            </Show>

            <Show when={props.primaryContactId() === null && props.accountId() !== null}>
              <p class="text-sm opacity-70 mb-3">
                No primary contact on this deal. Pick from the account's contacts:
              </p>
              <Show when={accountContacts.isLoading}>
                <p class="text-sm opacity-60 py-4">Loading contacts…</p>
              </Show>
              <Show
                when={!accountContacts.isLoading && (accountContacts.data?.items.length ?? 0) === 0}
              >
                <p class="text-sm opacity-60 py-4">The linked account has no contacts yet.</p>
              </Show>
              <ul class="divide-y divide-base-200 max-h-80 overflow-y-auto">
                <For each={accountContacts.data?.items ?? []}>
                  {(link) => (
                    <li>
                      <button
                        type="button"
                        class="w-full text-left px-3 py-2 hover:bg-base-200"
                        onClick={() =>
                          setStage({
                            kind: "drafting",
                            conId: link.contact_id,
                            contactLabel: link.contact_full_name,
                          })
                        }
                      >
                        <div class="font-medium">{link.contact_full_name}</div>
                        <Show when={link.contact_title || link.contact_primary_email}>
                          <div class="text-xs opacity-60">
                            {[link.contact_title, link.contact_primary_email]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </Show>
                      </button>
                    </li>
                  )}
                </For>
              </ul>
            </Show>

            <div class="modal-action">
              <button type="button" class="btn btn-ghost" onClick={props.onClose}>
                Cancel
              </button>
            </div>
          </div>
        </dialog>
      </Show>

      <Show when={stage().kind === "drafting"}>
        {(_) => {
          const s = stage() as Extract<Stage, { kind: "drafting" }>;
          return (
            <DraftEmailModal
              wsKey={props.wsKey}
              conId={() => s.conId}
              contactLabel={() => s.contactLabel}
              contextDealId={props.dealId}
              contextAccountId={() => props.accountId()}
              onClose={props.onClose}
            />
          );
        }}
      </Show>
    </>
  );
}
